import { API, graphqlOperation } from 'aws-amplify';
import { 
  listLocationEmployees,
  listOrgUnitEmployees,
  getLocation,
  getOrgUnit,
  getRoleDefinition
} from 'graphql/queries';
import * as logger from 'utils/logger';

/**
 * Fetches all location assignments for an employee
 * 
 * This function retrieves LocationEmployee records and enriches them with current data.
 * It uses the locationNameSnapshot and roleNameSnapshot fields for display, but for
 * active assignments, it can optionally fetch the current names from the database
 * to ensure accuracy if the location or role has been renamed.
 * 
 * @param {string} employeeId - The employee ID
 * @param {string} clientId - The client ID
 * @returns {Promise<Array>} Array of location assignment records with enriched data
 * @throws {Error} If the GraphQL query fails
 */
export const fetchLocationAssignments = async (employeeId, clientId) => {
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employeeId is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const filter = {
      employeeId: { eq: employeeId },
      clientId: { eq: clientId }
    };

    const result = await API.graphql(
      graphqlOperation(listLocationEmployees, { 
        filter,
        limit: 200 
      })
    );

    const assignments = result.data.listLocationEmployees.items || [];
    
    logger.info('LocationEmployee API Results', {
      employeeId,
      clientId,
      count: assignments.length
    });
    
    // Configuration flag: Set to true to always fetch current names from database
    const ALWAYS_FETCH_CURRENT_NAMES = true; // Set to false to use snapshots only
    
    // Enrich assignments with location and role details
    const enrichedAssignments = await Promise.all(
      assignments.map(async (assignment) => {
        try {
          // Start with snapshot name as default
          let locationName = assignment.locationNameSnapshot || 'Unknown Location';
          
          // For active assignments, optionally fetch current name from database
          if (ALWAYS_FETCH_CURRENT_NAMES && assignment.isActive !== false && assignment.locationId) {
            try {
              const locationResult = await API.graphql(
                graphqlOperation(getLocation, { id: assignment.locationId })
              );
              if (locationResult.data.getLocation) {
                // Use current name from database if available
                locationName = locationResult.data.getLocation.locationName || 
                              locationResult.data.getLocation.name || 
                              locationName;
                logger.debug('Updated location name from database', {
                  locationId: assignment.locationId,
                  snapshot: assignment.locationNameSnapshot,
                  current: locationName
                });
              }
            } catch (error) {
              logger.warn('Failed to fetch current location name, using snapshot', {
                locationId: assignment.locationId,
                error: error.message
              });
              // Fall back to snapshot name on error
            }
          }

          // Use roleSnapshot for the role name
          let roleName = assignment.roleSnapshot || assignment.roleNameSnapshot || 'Team Member';
          
          // Optionally fetch current role definition for active assignments
          if (ALWAYS_FETCH_CURRENT_NAMES && assignment.isActive !== false && assignment.roleDefinitionId) {
            try {
              const roleResult = await API.graphql(
                graphqlOperation(getRoleDefinition, { id: assignment.roleDefinitionId })
              );
              if (roleResult.data.getRoleDefinition) {
                roleName = roleResult.data.getRoleDefinition.name || roleName;
              }
            } catch (error) {
              logger.warn('Failed to fetch role definition, using snapshot', {
                roleDefinitionId: assignment.roleDefinitionId,
                error: error.message
              });
            }
          }

          return {
            ...assignment,
            type: 'location',
            locationName,
            roleName,
            startDate: assignment.effectiveDate || assignment.startDate,
            endDate: assignment.endDate,
            isActive: assignment.isActive !== false,
            isPrimary: assignment.isPrimary === true,
            weeklyHours: assignment.weeklyHours || null,
            shift: assignment.shift || null,
            assignedBy: assignment.assignedBy || assignment.createdBy || null,
            assignmentReason: assignment.assignmentReason || assignment.reason || null,
            changeReason: assignment.changeReason || null,
            changedBy: assignment.changedBy || null,
            // Keep snapshot for reference
            _snapshot: {
              locationName: assignment.locationNameSnapshot,
              roleName: assignment.roleSnapshot || assignment.roleNameSnapshot
            }
          };
        } catch (error) {
          logger.warn('Failed to enrich location assignment', { assignment, error });
          return {
            ...assignment,
            type: 'location',
            locationName: assignment.locationNameSnapshot || 'Unknown Location',
            roleName: assignment.roleSnapshot || assignment.roleNameSnapshot || 'Unknown Role',
            startDate: assignment.effectiveDate || assignment.startDate,
            endDate: assignment.endDate,
            isActive: assignment.isActive !== false,
            isPrimary: assignment.isPrimary === true,
            weeklyHours: assignment.weeklyHours || null,
            shift: assignment.shift || null,
            assignedBy: assignment.assignedBy || assignment.createdBy || null,
            assignmentReason: assignment.assignmentReason || assignment.reason || null,
            changeReason: assignment.changeReason || null,
            changedBy: assignment.changedBy || null
          };
        }
      })
    );

    return enrichedAssignments;
  } catch (error) {
    logger.error('Failed to fetch location assignments', { employeeId, clientId, error });
    throw new Error('Unable to fetch location assignments. Please try again.');
  }
};

/**
 * Fetches all organizational unit assignments for an employee
 * 
 * This function retrieves OrgUnitEmployee records and enriches them with current data.
 * It uses the orgUnitNameSnapshot and roleNameSnapshot fields for display, but for
 * active assignments, it can optionally fetch the current names from the database
 * to ensure accuracy if the org unit has been renamed or restructured.
 * 
 * @param {string} employeeId - The employee ID
 * @param {string} clientId - The client ID
 * @returns {Promise<Array>} Array of org unit assignment records with enriched data
 * @throws {Error} If the GraphQL query fails
 */
export const fetchOrgUnitAssignments = async (employeeId, clientId) => {
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employeeId is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const filter = {
      employeeId: { eq: employeeId },
      clientId: { eq: clientId }
    };

    const result = await API.graphql(
      graphqlOperation(listOrgUnitEmployees, { 
        filter,
        limit: 200 
      })
    );

    const assignments = result.data.listOrgUnitEmployees.items || [];
    
    logger.info('OrgUnitEmployee API Results', {
      employeeId,
      clientId,
      count: assignments.length
    });
    
    // Configuration flag: Set to true to always fetch current names from database
    const ALWAYS_FETCH_CURRENT_NAMES = true; // Set to false to use snapshots only
    
    // Enrich assignments with org unit details
    const enrichedAssignments = await Promise.all(
      assignments.map(async (assignment) => {
        try {
          // Start with snapshot name as default
          let orgUnitName = assignment.orgUnitNameSnapshot || 'Unknown Department';
          let orgLevel = assignment.orgLevel || assignment.managementLevel || 'ORGLEVEL_1';
          
          // For active assignments, optionally fetch current name and orgLevel from database
          if (ALWAYS_FETCH_CURRENT_NAMES && assignment.isActive !== false && assignment.orgUnitId) {
            try {
              const orgUnitResult = await API.graphql(
                graphqlOperation(getOrgUnit, { id: assignment.orgUnitId })
              );
              if (orgUnitResult.data.getOrgUnit) {
                const orgUnit = orgUnitResult.data.getOrgUnit;
                // Use current name from database if available
                orgUnitName = orgUnit.name || orgUnitName;
                // Get the actual org level from the org unit
                orgLevel = orgUnit.orgLevel || orgLevel;
                
                logger.debug('Updated org unit details from database', {
                  orgUnitId: assignment.orgUnitId,
                  snapshot: assignment.orgUnitNameSnapshot,
                  currentName: orgUnitName,
                  orgLevel: orgLevel
                });
              }
            } catch (error) {
              logger.warn('Failed to fetch current org unit details, using snapshot', {
                orgUnitId: assignment.orgUnitId,
                error: error.message
              });
              // Fall back to snapshot name on error
            }
          }

          // Use roleSnapshot for the role name
          const managementLevels = {
            'EXECUTIVE': 'Executive',
            'DIRECTOR': 'Director',
            'MANAGER': 'Manager',
            'SUPERVISOR': 'Supervisor',
            'TEAM_LEAD': 'Team Lead',
            'INDIVIDUAL': 'Team Member'
          };
          
          // Use roleSnapshot if available, otherwise derive from management level
          let roleName = assignment.roleSnapshot || 
                        assignment.roleNameSnapshot || 
                        managementLevels[assignment.managementLevel] || 
                        assignment.managementLevel || 
                        'Team Member';

          return {
            ...assignment,
            type: 'orgUnit',
            orgUnitName,
            orgUnitId: assignment.orgUnitId,
            department: orgUnitName,
            managementLevel: assignment.managementLevel || 'ORGLEVEL_1',
            orgLevel,  // Use the org level from the org unit or fallback
            roleName,
            startDate: assignment.effectiveDate || assignment.startDate,
            endDate: assignment.endDate,
            isActive: assignment.isActive !== false,
            isPrimary: assignment.isPrimary === true,
            assignedBy: assignment.assignedBy || assignment.createdBy || null,
            assignmentReason: assignment.assignmentReason || assignment.reason || null,
            changeReason: assignment.changeReason || null,
            changedBy: assignment.changedBy || null,
            // Keep snapshot for reference
            _snapshot: {
              orgUnitName: assignment.orgUnitNameSnapshot,
              roleName: assignment.roleSnapshot || assignment.roleNameSnapshot
            }
          };
        } catch (error) {
          logger.warn('Failed to enrich org unit assignment', { assignment, error });
          return {
            ...assignment,
            type: 'orgUnit',
            orgUnitName: assignment.orgUnitNameSnapshot || 'Unknown Department',
            orgUnitId: assignment.orgUnitId,
            department: assignment.orgUnitNameSnapshot || 'Unknown Department',
            managementLevel: assignment.managementLevel || 'ORGLEVEL_1',
            orgLevel: assignment.orgLevel || assignment.managementLevel || 'ORGLEVEL_1',
            roleName: assignment.roleSnapshot || assignment.roleNameSnapshot || 'Team Member',
            startDate: assignment.effectiveDate || assignment.startDate,
            endDate: assignment.endDate,
            isActive: assignment.isActive !== false,
            isPrimary: assignment.isPrimary === true,
            assignedBy: assignment.assignedBy || assignment.createdBy || null,
            assignmentReason: assignment.assignmentReason || assignment.reason || null,
            changeReason: assignment.changeReason || null,
            changedBy: assignment.changedBy || null
          };
        }
      })
    );

    return enrichedAssignments;
  } catch (error) {
    logger.error('Failed to fetch org unit assignments', { employeeId, clientId, error });
    throw new Error('Unable to fetch organizational assignments. Please try again.');
  }
};

/**
 * Fetches all assignments (both location and org unit) for an employee
 * @param {string} employeeId - The employee ID
 * @param {string} clientId - The client ID
 * @returns {Promise<Array>} Combined array of all assignment records
 * @throws {Error} If the GraphQL queries fail
 */
export const fetchAllAssignments = async (employeeId, clientId) => {
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employeeId is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    // Fetch both types of assignments in parallel
    const [locationAssignments, orgUnitAssignments] = await Promise.all([
      fetchLocationAssignments(employeeId, clientId),
      fetchOrgUnitAssignments(employeeId, clientId)
    ]);

    // Combine and sort by start date (most recent first)
    const allAssignments = [...locationAssignments, ...orgUnitAssignments];
    allAssignments.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
      const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
      return dateB - dateA;
    });

    logger.debug('Successfully fetched all assignments', {
      employeeId,
      locationCount: locationAssignments.length,
      orgUnitCount: orgUnitAssignments.length,
      totalCount: allAssignments.length
    });

    return allAssignments;
  } catch (error) {
    logger.error('Failed to fetch all assignments', { employeeId, clientId, error });
    throw new Error('Unable to fetch assignments. Please try again.');
  }
};

/**
 * Counts assignments for an employee without fetching full details
 * @param {string} employeeId - The employee ID
 * @param {string} clientId - The client ID
 * @returns {Promise<number>} Total count of assignments
 */
export const getAssignmentCount = async (employeeId, clientId) => {
  if (!employeeId || typeof employeeId !== 'string') {
    return 0;
  }
  if (!clientId || typeof clientId !== 'string') {
    return 0;
  }

  try {
    const filter = {
      employeeId: { eq: employeeId },
      clientId: { eq: clientId }
    };

    // Fetch counts in parallel
    const [locationResult, orgUnitResult] = await Promise.all([
      API.graphql(
        graphqlOperation(listLocationEmployees, { 
          filter,
          limit: 200 
        })
      ),
      API.graphql(
        graphqlOperation(listOrgUnitEmployees, { 
          filter,
          limit: 200 
        })
      )
    ]);

    const locationCount = locationResult.data.listLocationEmployees.items?.length || 0;
    const orgUnitCount = orgUnitResult.data.listOrgUnitEmployees.items?.length || 0;

    return locationCount + orgUnitCount;
  } catch (error) {
    logger.warn('Failed to get assignment count', { employeeId, clientId, error });
    return 0;
  }
};

/**
 * Fetches assignments for multiple employees in batch
 * @param {Array<string>} employeeIds - Array of employee IDs
 * @param {string} clientId - The client ID
 * @returns {Promise<Object>} Map of employeeId to assignment count
 */
export const getAssignmentCounts = async (employeeIds, clientId) => {
  if (!employeeIds || !Array.isArray(employeeIds) || employeeIds.length === 0) {
    return {};
  }
  if (!clientId || typeof clientId !== 'string') {
    return {};
  }

  try {
    // Create a map to store results
    const counts = {};
    
    // Initialize all counts to 0
    employeeIds.forEach(id => {
      counts[id] = 0;
    });

    // Fetch all location assignments for the client
    const locationFilter = {
      clientId: { eq: clientId }
    };
    
    const locationResult = await API.graphql(
      graphqlOperation(listLocationEmployees, { 
        filter: locationFilter,
        limit: 1000 
      })
    );

    // Count location assignments per employee
    const locationAssignments = locationResult.data.listLocationEmployees.items || [];
    locationAssignments.forEach(assignment => {
      if (employeeIds.includes(assignment.employeeId)) {
        counts[assignment.employeeId] = (counts[assignment.employeeId] || 0) + 1;
      }
    });

    // Fetch all org unit assignments for the client
    const orgUnitResult = await API.graphql(
      graphqlOperation(listOrgUnitEmployees, { 
        filter: locationFilter,
        limit: 1000 
      })
    );

    // Count org unit assignments per employee
    const orgUnitAssignments = orgUnitResult.data.listOrgUnitEmployees.items || [];
    orgUnitAssignments.forEach(assignment => {
      if (employeeIds.includes(assignment.employeeId)) {
        counts[assignment.employeeId] = (counts[assignment.employeeId] || 0) + 1;
      }
    });

    logger.debug('Successfully fetched assignment counts', {
      employeeCount: employeeIds.length,
      totalLocationAssignments: locationAssignments.length,
      totalOrgUnitAssignments: orgUnitAssignments.length
    });

    return counts;
  } catch (error) {
    logger.warn('Failed to get assignment counts', { clientId, error });
    // Return empty counts on error
    const counts = {};
    employeeIds.forEach(id => {
      counts[id] = 0;
    });
    return counts;
  }
};